import React, { useEffect, useState } from 'react';
import Page from 'components/Page';
import { Box, Button, ButtonGroup, CircularProgress, ClickAwayListener, Divider, Grid, Grow, MenuItem, MenuList, Paper, Popper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import PageHeader from 'global/PageHeader';
import Notificationbar from 'global/Notificationbar';
import { DateRangePicker } from 'rsuite';
import CTTabs from 'components/CTTabs';
import BarChart from 'components/Charts/barchart';
import { formatAppointmentDateOnly, formatCurrency } from 'utils/formatter';
import PieChart from 'components/Charts/piechart';
import ActivityGrid from 'components/Charts/activity-grid';
import * as ReportService from 'services/report-service';
import * as OTRReportService from 'services/otr/otr-report-service';
import ReportSummary from './report-summary';
import { DataGrid } from '@mui/x-data-grid';
import DataDialog from 'components/dialogs/DataDialog';
import { connect } from 'react-redux';
import { tokens } from 'theme/tokens';
import ChartContainer from 'components/Charts/chart-container';
import { CHART_COLORS } from 'utils/constant';

const TABS = ['Dispatch Report'];
const QUARTERS = ['FIRST QUARTER', 'SECOND QUARTER', 'THIRD QUARTER', 'FORTH QUARTER'];
const MONTHLY = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
const YEARLY = ['2024', '2025'];

const ReportPage = ((props) => {
  /* Theme */
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { tenantBusinessModel } = props.tenant;
  const mode = tenantBusinessModel === 'ALL' ? 'INTERMODAL' : tenantBusinessModel;
  const startOfMonthDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);  
  const today = new Date(new Date().setHours(0, 0, 0, 0)).toISOString().slice(0, 10);

  const [tabIndex, setTabIndex] = useState(0);
  const [dateRange, setDateRange] = useState(null);
  const [startDate, setStartDate] = useState(startOfMonthDate)
  const [endDate, setEndDate] = useState(today);
  const [anchorRef, setAnchorRef] = useState(null);
  const [isMonthlyMenuOpen, setMonthlyMenuOpen] = useState(false);
  const [isYearlyMenuOpen, setYearlyMenuOpen] = useState(false);
  const [isDateOptionMenuOpen, setDateOptionMenuOpen] = useState(false);
  const [isQuarterMenuOpen, setQuarterMenuOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [toast, setToast] = useState({});
  const [transportationMode, setTransportationMode] = useState(mode);

  /* Reports */
  /* Summary Stats */
  const [orderSummaryStats, setOrderSummaryStats] = useState(null);
  const [invoiceSummaryStats, setInvoiceSummaryStats] = useState(null);
  const [billSummaryStats, setBillSummaryStats] = useState(null);
  const [netStats, setNetStats] = useState(null);
  const [invoiceCountStats, setInvoiceCountStats] = useState(null);
  const [billCountStats, setBillCountStats] = useState(null);
  const [operationalExpenseStats, setOperationalExpenseStats] = useState(null);

  /* Time Series */
  const [consolidatedDataByTS, setConsolidatedDataByTS] = useState(null);

  /* PieChart */
  const [expenseAndChargesData, setExpenseAndChargesData] = useState(null);//Expenses & Charges  
  const [orderTypeCountsData, setOrderTypeCountsData] = useState(null); //Order Type

  /* Tabular Data */
  const [driverPaymentStats, setDriverPaymentStats] = useState(null);
  const [brokerAccountingStats, setBrokerAccountingStats] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [charges, setCharges] = useState([]);

  /* Activity Chart */
  const [orderCountByDate, setOrderCountByDate] = useState(null);

  /* Top N */
  const [topNDestinations, setTopNDestinations] = useState(null);
  const [topNOrigins, setTopNOrigins] = useState(null);

  /* Map */
  // const [originGeoData, setOriginGeoData] = useState(null);
  // const [destinationGeoData, setDestinationGeoData] = useState(null);

  /* Dialog */
  const [isDriverPaymentDialogOpen, setDriverPaymentDialog] = useState(false);
  const [isBrokerInvoiceDialogOpen, setBrokerInvoiceDialog] = useState(false);
  const [isDeliveryDialogOpen, setDeliveryDialog] = useState(false);
  const [isPickupDialogOpen, setPickupDialog] = useState(false);
  const [isExpenseChargeDialogOpen, setExpenseChargeDialog] = useState(false);

  const locationColumns = [
    { field: 'label', headerName: 'Name', flex: 1 },
    { field: 'value', headerName: 'Count', flex: 1 }
  ];

  const expenseChargeColumns = [
    { field: 'date', headerName: 'Date', flex: 1 },
    { field: 'count', headerName: 'Count', flex: 1 },
    { field: 'amount', headerName: 'Amount', flex: 1 }
  ];

  const driverSettlementColumns = [
    { field: 'id', headerName: 'Id', flex: .5 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'orderCount', headerName: 'Order Count', flex: 1 },
    {
      field: 'totalAmount', headerName: 'Amount', flex: 1, renderCell: (params) => {
        return formatCurrency(params.row.totalAmount);
      }
    },
    {
      field: 'paid', headerName: 'Payment Made', flex: 1, renderCell: (params) => {
        return formatCurrency(params.row.paid);
      }
    }
  ];
  const brokerColumns = [
    { field: 'id', headerName: 'Id', flex: .5 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'orderCount', headerName: 'Order Count', flex: 1 },
    {
      field: 'totalAmount', headerName: 'Amount', flex: 1, renderCell: (params) => {
        return formatCurrency(params.row.totalAmount);
      }
    },
    {
      field: 'paid', headerName: 'Payment Received', flex: 1, renderCell: (params) => {
        return formatCurrency(params.row.paid);
      }
    },
  ];

  useEffect(() => {
    if (transportationMode) {
      prepareReport(startDate, endDate, transportationMode);
    }
  }, [startDate, endDate, transportationMode]);

  const handleDateRangeChange = (evt) => {
    if (evt !== null && evt.length === 2) {
      const start = evt[0].toISOString().substring(0, 10);
      const end = evt[1].toISOString().substring(0, 10);
      setDateRange(evt);
      setStartDate(start);
      setEndDate(end)
    }
  };

  const handleQuarterChange = (option) => {
    var quarter = 0;
    if (option === QUARTERS[1]) { //Second 
      quarter = 1;
    } else if (option === QUARTERS[2]) { // Third
      quarter = 2;
    } else if (option === QUARTERS[3]) {
      quarter = 3;
    }
    const quarterStartDate = new Date(new Date().getFullYear(), quarter * 3, 1).toISOString().slice(0, 10);
    const quarterEndDate = new Date(new Date().getFullYear(), quarter * 3 + 3, 1).toISOString().slice(0, 10);
    setStartDate(quarterStartDate);
    setEndDate(quarterEndDate);
  };

  const handleMonthChange = (month) => {
    const monthNumber = MONTHLY.indexOf(month);
    const date = new Date();
    const year = date.getFullYear();
    const firstDay = new Date(year, monthNumber, 1).toISOString().slice(0, 10);
    const lastDay = new Date(year, monthNumber + 1, 0).toISOString().slice(0, 10);
    setStartDate(firstDay);
    setEndDate(lastDay);
  }

  const handleYearChange = (year) => {  
    const firstDay = new Date(year, 1, 1).toISOString().slice(0, 10);
    const lastDay = new Date(year, 12, 0).toISOString().slice(0, 10);
    setStartDate(firstDay);
    setEndDate(lastDay);
  }

  const prepareReport = async (startDate, endDate, tenantBusinessModel) => {
    const reportService = transportationMode === 'OTR' ? OTRReportService : ReportService;
    setIsProcessing(true);
    await Promise.all([
      reportService.getReportByType('STATS', startDate, endDate).then(handleSummaryStats),
      reportService.getReportByType('ORDERNET', startDate, endDate).then(handleOrderNetTimeSeries),
      reportService.getReportByType('EXPENSESANDCHARGES', startDate, endDate).then(handleExpenseChargeData),
      reportService.getReportByType('ORDERTYPE', startDate, endDate).then(handleOrderTypeData),
      reportService.getReportByType('PAYMENTS', startDate, endDate).then(handleDriverPaymentStats),
      reportService.getReportByType('INVOICES', startDate, endDate).then(handleBrokerAccountingStats),
      reportService.getReportByType('ACTIVITY', startDate, endDate).then(handleOrderCountActivity),
      reportService.getReportByType('TOP', startDate, endDate).then(handleTopLocations),
    ]);
  };

  /* Response Handlers */
  const handleSummaryStats = (response) => {
    const { data, status } = response;
    if (status === 200) {
      setIsProcessing(false);
      setOrderSummaryStats(data.orderCountByStatus);
      setInvoiceSummaryStats(data.invoiceAmountByStatus);
      setInvoiceCountStats(data.invoiceCountByStatus)
      setBillSummaryStats(data.driverPaymentByStatus);
      setBillCountStats(data.billCountByStatus);
      setOperationalExpenseStats(data.operationalExpenses);
      setNetStats(data.netStats);
    }
  };
  const handleOrderNetTimeSeries = (response) => {
    const { data, status } = response;
    const result = [];
    if (status === 200) {
      const { orderNetByCompleteDateTS, billAmountByDateTS, invoiceAmountByDateTS } = data;
      Object.entries(invoiceAmountByDateTS).forEach((pair) => {
        const key = pair[0];
        const billAmount = billAmountByDateTS[key] ? Number(billAmountByDateTS[key]).toFixed() : 0;
        const invoiceAmount = pair[1] ? Number(pair[1]).toFixed() : 0 ;
        const net = orderNetByCompleteDateTS[key] ? Number(orderNetByCompleteDateTS[key]).toFixed() : 0;
        // if (billAmount && invoiceAmount) {
        result.push({ 'Appointment': formatAppointmentDateOnly(Number(key)), 'Invoice': Number(billAmount), 'Payment': Number(invoiceAmount), 'OrderNet': net ? Number(net) : 0 });
        //}
      });
    }
    setConsolidatedDataByTS(result);
  };

  const handleExpenseChargeData = (response) => {
    const { data, status } = response;
    if (status === 200) {
      const charges = data.expenseAndCharges.charges;
      const expenses = data.expenseAndCharges.expenses;
      const totalCharges = charges.reduce((acc, item) => { return acc += item.amount; }, 0);
      const totalExpenses = expenses.reduce((acc, item) => { return acc += item.amount; }, 0);

      const result = [
        { id: 'CHARGES', label: 'CHARGES', value: Number(totalCharges), count: charges.length },
        { id: 'EXPENSES', label: 'EXPENSES', value: Number(totalExpenses), count: charges.length }
      ];
      setExpenseAndChargesData(result);
      setExpenses(expenses.map((exp, idx) => {
        return ({ 'id': idx + 1, 'date': exp.localDateTS, 'count': exp.count, 'amount': exp.amount })
      }));
      setCharges(charges.map((chr, idx) => {
        return ({ 'id': idx + 1, 'date': chr.localDateTS, 'count': chr.count, 'amount': chr.amount })
      }));
    }
  };
  const handleOrderTypeData = (response) => {
    const { data, status } = response;
    if (status === 200) {
      const result = [];
      Object.entries(data.orderTypeCounts).forEach(pair => {
        const label = pair[0];
        const value = pair[1];

        result.push({ 'id': label, 'label': label, 'count': Number(value) });
      });
      setOrderTypeCountsData(result);
    }
  };

  const handleDriverPaymentStats = (response) => {
    const { data, status } = response;
    if (status === 200) {
      data.driverAccountingStats.forEach((item, idx) => item.id = idx);
      setDriverPaymentStats(data.driverAccountingStats);
    }
  };

  const handleBrokerAccountingStats = (response) => {
    const { data, status } = response;
    if (status === 200) {
      data.brokerAccountingStats.forEach((item, idx) => item.id = idx);
      setBrokerAccountingStats(data.brokerAccountingStats);
    }
  };

  const handleOrderCountActivity = (response) => {
    const { data, status } = response;
    if (status === 200) {
      const result = [];
      data.orderCountByDay.forEach(item => {
        const key = item.dateTS;
        const value = item.count;
        if (key) {
          result.push({ 'day': key, 'value': Number(value) });
        }
      });
      setOrderCountByDate(result);
    }
  };

  const handleTopLocations = (response) => {
    const { data, status } = response;
    if (status === 200) {
      const origins = []
      const destinations = [];
      const { topNOrigins, topNDestinations } = data;
      Object.entries(topNOrigins).forEach((pair, idx) => {
        origins.push({ 'Name': pair[0], 'Count': pair[1] });
      });
      Object.entries(topNDestinations).forEach((pair, idx) => {
        destinations.push({ 'Name': pair[0], 'Count': pair[1] });
      });
      setTopNOrigins(origins);
      setTopNDestinations(destinations);
    }
  };

  const closeDriverBrokerDialog = () => {
    setDriverPaymentDialog(false);
    setBrokerInvoiceDialog(false);
  };

  const closeLocationDialog = () => {
    setDeliveryDialog(false);
    setPickupDialog(false);
  };

  /* Invoice & Payment Breakdown */
  const getInvoiceBreakdown = () => {
    const invoiceData = invoiceSummaryStats && Object.entries(invoiceSummaryStats)
      .map(([key, value]) => {
        return { id: key, label: key, value: value, count: invoiceCountStats && invoiceCountStats[key] }
      });
    return invoiceData;
  };
  const getPaymentBreakdown = () => {
    const paymentDate = billSummaryStats && Object.entries(billSummaryStats)
      .map(([key, value]) => {
        return { id: key, label: key, value: value, count: billCountStats && billCountStats[key] }
      });
    return paymentDate;
  };

  const getOrderBreakdown = () => {
    const orderCountData = orderSummaryStats && Object.entries(orderSummaryStats)
      .map(([key, value]) => ({ id: key, label: key, value: value, count: value }));
    return orderCountData;
  }

  const invoiceBreakdownPieChart = getInvoiceBreakdown();
  const paymentBreakdownPieChart = getPaymentBreakdown();
  const orderCountBreakdownPieChart = getOrderBreakdown();

  return (
    <Page title='Dashboard'>
      <Box display={'flex'} justifyContent={'space-between'}>
        <PageHeader
          title='Insights'
          subTitle='View your dispatch summary'
        />
        <Notificationbar />
      </Box>
      <Box
        m='20px 0 0 0'
        height='75vh'
      >
        <Box display={'flex'} justifyContent={'space-between'}>
          <Box
            display={'flex'}
            mb={1}
          >
            {tenantBusinessModel === 'ALL' &&
              <Tabs
                value={transportationMode}
                onChange={(evt, value) => setTransportationMode(value)}
                textColor={colors.base.white}
                indicatorColor='primary'
                sx={{
                  background: colors.neutral[100],
                  borderRadius: '4px',
                  padding: '4px',
                  height: 6,
                  gap: '4px',

                }}
              >
                {['INTERMODAL', 'OTR'].map((mode, idx) => (
                  <Tab
                    key={`${idx}-${mode}`}
                    value={mode}
                    label={mode === 'INTERMODAL' ? 'Intermodal' : 'OTR'}
                    sx={{
                      minHeight: '40px',
                      padding: '6px 16px',
                      borderRadius: '4px',
                      fontWeight: 500,
                      backgroundColor:
                        transportationMode === mode ? colors.base.white : 'transparent',
                      borderBottom: '1px solid',
                      borderBottomColor:
                        transportationMode === mode ? colors.base.white : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          transportationMode === mode
                            ? colors.primary.dark
                            : colors.neutral[200],
                      },
                    }}
                  />
                ))
                };
              </Tabs>
            }
          </Box>
          <Box>
            <DateRangePicker
              format='MMM dd, yyyy'
              value={dateRange}
              onChange={handleDateRangeChange}
            />
            &nbsp;&nbsp;
            <ButtonGroup
              variant='outlined'
              color='secondary'
            >
              <Button onClick={(evt) => {
                setDateOptionMenuOpen(true);
                setMonthlyMenuOpen(true);
                setAnchorRef(evt.currentTarget);
              }}>Monthly</Button>
              <Button onClick={(evt) => {
                setDateOptionMenuOpen(true);
                setQuarterMenuOpen(true);
                setAnchorRef(evt.currentTarget);
              }}>Quarterly</Button>
              <Button onClick={(evt) => {
                setDateOptionMenuOpen(true);
                setYearlyMenuOpen(true);
                setAnchorRef(evt.currentTarget);
              }}>Yearly</Button>
            </ButtonGroup>
            <Popper
              open={isDateOptionMenuOpen}
              anchorEl={anchorRef}
              modifiers={{
                arrow: {
                  enabled: true,
                  element: anchorRef
                }
              }}
              transition disablePortal style={{ zIndex: 1500 }}>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper elevation={2}>
                    <ClickAwayListener onClickAway={() => {
                      setDateOptionMenuOpen(false);
                      setQuarterMenuOpen(false);
                      setMonthlyMenuOpen(false);
                    }}>
                      <MenuList id='split-button-menu'>
                        {isQuarterMenuOpen && QUARTERS.map((option) => (
                          <>
                            <MenuItem
                              key={option}
                              onClick={() => handleQuarterChange(option)}
                            >
                              <Typography variant='body1'>{option}</Typography>
                            </MenuItem>
                            <Divider />
                          </>
                        ))}
                        {isMonthlyMenuOpen && MONTHLY.map((option) => (
                          <>
                            <MenuItem
                              key={option}
                              onClick={() => handleMonthChange(option)}
                            >
                              <Typography variant='body1'>{option}</Typography>
                            </MenuItem>
                            <Divider />
                          </>
                        ))}
                        {isYearlyMenuOpen && YEARLY.map((option) => (
                          <>
                            <MenuItem
                              key={option}
                              onClick={() => handleYearChange(option)}
                            >
                              <Typography variant='body1'>{option}</Typography>
                            </MenuItem>
                            <Divider />
                          </>
                        ))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        </Box>
        <Typography variant='body1'
          style={{
            letterSpacing: 1,
            color: colors.neutral[500],
            textTransform: 'uppercase',
            marginTop: 0.5,
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          {`View Report from ${startDate} to ${endDate}`}
        </Typography>
        <CTTabs
          labels={TABS}
          tabIndex={tabIndex}
        // fnOnTabChangeHandler={this.changeTab}
        />
        {isProcessing && <CircularProgress style={{ margin: '0px 50%' }} />}

        {/* SUMMARY STATS */}
        <ReportSummary
          orderSummaryStats={orderSummaryStats}
          invoiceSummaryStats={invoiceSummaryStats}
          invoiceCountStats={invoiceCountStats}
          billSummaryStats={billSummaryStats}
          billCountStats={billCountStats}
          operationalExpenses={operationalExpenseStats}
          netStats={netStats || {}}
        />
        {/* TIMESERIES */}
        <Box
          mt={1}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <ChartContainer
                title='Accounting Stats'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 260 }}>
                  <BarChart
                    data={consolidatedDataByTS || []}
                    keys={['Invoice', 'Payment', 'OrderNet']}
                    indexKey={'Appointment'}
                    groupMode={'grouped'}
                    layout={'vertical'}
                    margin={{ top: 20, right: 60, bottom: 40, left: 60 }}
                    valueFormat={false}
                    valueScale={{ type: 'linear' }}
                    axisBottom={true}
                    axisBottomLegend={'Appointment'}
                  />
                </Box>
              </ChartContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Orders'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 200 }}>
                  <PieChart
                    data={orderCountBreakdownPieChart || []}
                    customColor={true}
                  />
                </Box>
                <Grid container spacing={1}>
                  {orderCountBreakdownPieChart && orderCountBreakdownPieChart.map(item => (
                    <Grid key={item.label} size={{ xs: 4, sm: 4 }}>
                      <Typography variant='body1' sx={{ marginLeft: 1 }}>
                        <span style={{
                          border: `3px solid ${CHART_COLORS[item.label]}`,
                          height: '12px',
                          display: 'inline-block',
                          margin: '0px 5px',
                        }}></span>
                        {item.label}&nbsp;&nbsp;{item.count}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>

        {/* ACCOUNTING PIE CHART BREAKDOWN */}
        <Box
          mt={1}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Invoice Breakdown'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 180 }}>
                  <PieChart
                    data={invoiceBreakdownPieChart || []}
                    customColor={true}
                  />
                </Box>
                {invoiceBreakdownPieChart && invoiceBreakdownPieChart.map(item => (
                  <Grid container spacing={1} key={item.label}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography variant='body1' sx={{ marginLeft: 1 }}>
                        <span style={{
                          border: `3px solid ${CHART_COLORS[item.label]}`,
                          height: '12px',
                          display: 'inline-block',
                          margin: '0px 5px',
                        }}></span>
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{item.count}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{formatCurrency(item.value, 'en-US', 'compact')}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </ChartContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Payment Breakdown'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 180 }}>
                  <PieChart
                    data={paymentBreakdownPieChart || []}
                    customColor={true}
                  />
                </Box>
                {paymentBreakdownPieChart && paymentBreakdownPieChart.map(item => (
                  <Grid container spacing={1} key={item.label}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography variant='body1' sx={{ marginLeft: 1 }}>
                        <span style={{
                          border: `3px solid ${CHART_COLORS[item.label]}`,
                          height: '12px',
                          display: 'inline-block',
                          margin: '0px 5px',
                        }}></span>
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{item.count}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{formatCurrency(item.value, 'en-US', 'compact')}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </ChartContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Expense Breakdown'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 180 }}>
                  <PieChart
                    data={expenseAndChargesData || []}
                    customColor={true}
                  />
                </Box>
                {expenseAndChargesData && expenseAndChargesData.map(item => (
                  <Grid container spacing={1} key={item.label}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography variant='body1' sx={{ marginLeft: 1 }}>
                        <span style={{
                          border: `3px solid ${CHART_COLORS[item.label]}`,
                          height: '12px',
                          display: 'inline-block',
                          margin: '0px 5px',
                        }}></span>
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{item.count}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{formatCurrency(item.value, 'en-US', 'compact')}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>

        {/* TOP 5 */}
        <Box
          mt={1}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Top 5 Destination'
                height='320px'
              >
                <Box sx={{ height: 200 }}>
                  <BarChart
                    data={topNDestinations && topNDestinations.slice(0, 5) || []}
                    keys={['Count']}
                    indexKey={'Name'}
                    groupMode={'stacked'}
                    layout={'horizontal'}
                    margin={{ top: 30, right: 80, bottom: 0, left: 160 }}
                    valueFormat={true}
                    valueScale={{ type: 'linear' }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Top 5 Origins'
                height='320px'
              >
                <Box sx={{ height: 200 }}>
                  <BarChart
                    data={topNOrigins && topNOrigins.slice(0, 5) || []}
                    keys={['Count']}
                    indexKey={'Name'}
                    groupMode={'stacked'}
                    layout={'horizontal'}
                    margin={{ top: 30, right: 80, bottom: 0, left: 160 }}
                    valueFormat={true}
                    valueScale={{ type: 'linear' }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ChartContainer
                title='Order Types'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 210 }}>
                  <PieChart
                    data={orderTypeCountsData || []}
                    customColor={true}
                  />
                </Box>
                {orderTypeCountsData && orderTypeCountsData.map(item => (
                  <Grid container spacing={1} key={item.label}>
                    <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                      <Typography variant='body1' sx={{ marginLeft: 1 }}>
                        <span style={{
                          border: `3px solid ${CHART_COLORS[item.label]}`,
                          height: '12px',
                          display: 'inline-block',
                          margin: '0px 5px',
                        }}></span>
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3, md: 3 }}>
                      <Typography variant='body1'>{item.count}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>

        {/* Accounting Details */}
        <Box
          mt={1}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ChartContainer
                title='Driver Settlement'
                height='320px'
              >
                <Box sx={{ width: 'auto', height: 200 }}>
                  <DataGrid
                    rows={driverPaymentStats && driverPaymentStats.slice(0, 5) || []}
                    columns={driverSettlementColumns}
                    autoHeight
                    density='compact'
                  />
                </Box>
                <Button size='small' color='secondary' onClick={() => setDriverPaymentDialog(true)}>VIEW MORE</Button>
              </ChartContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ChartContainer
                title='Customer Payment'
                height='320px'
              >
                <DataGrid
                  rows={brokerAccountingStats && brokerAccountingStats.slice(0, 5) || []}
                  columns={brokerColumns}
                  autoHeight
                  density='compact'
                />
                <Button size='small' color='secondary' onClick={() => setBrokerInvoiceDialog(true)}>VIEW MORE</Button>
              </ChartContainer>
            </Grid>
          </Grid>
        </Box>
        <Box
          mt={1}
        >
          <ChartContainer
            title='Order Types'
            height='280px'
          >
            <Box sx={{ width: 'auto', height: 220 }}>
              <ActivityGrid
                data={orderCountByDate || []}
                from={startDate}
                to={endDate}
              />
            </Box>
          </ChartContainer>
        </Box>
      </Box>
      {/* View more driver or broker payments */}
      <DataDialog
        open={isDriverPaymentDialogOpen || isBrokerInvoiceDialogOpen}
        title={isDriverPaymentDialogOpen ? 'Driver Settlement' : 'Broker Invoice'}
        onCloseHandler={closeDriverBrokerDialog}
      >
        <DataGrid
          rows={isDriverPaymentDialogOpen ? driverPaymentStats : brokerAccountingStats}
          columns={isDriverPaymentDialogOpen ? driverSettlementColumns : brokerColumns}
          autoHeight
          density='compact'
        />
      </DataDialog>
      {/* View more delivery or pickup locations */}
      <DataDialog
        open={isDeliveryDialogOpen || isPickupDialogOpen}
        title={isDeliveryDialogOpen ? 'Top Deliveries' : 'Top Pickups'}
        onCloseHandler={closeLocationDialog}
      >
        <DataGrid
          rows={isDeliveryDialogOpen ? topNDestinations : topNOrigins}
          columns={locationColumns}
          autoHeight
          density='compact'
        />
      </DataDialog>
      <DataDialog
        open={isExpenseChargeDialogOpen}
        title={'Expenses & Charges'}
        onCloseHandler={() => setExpenseChargeDialog(false)}
      >
        <DataGrid
          rows={expenses}
          columns={expenseChargeColumns}
          autoHeight
          density='compact'
        />
        <DataGrid
          rows={charges}
          columns={expenseChargeColumns}
          autoHeight
          density='compact'
        />
      </DataDialog>
    </Page>
  );
});

function mapStatesToProps(state) {
  const { tenant } = state.tenantReducer;
  return { tenant };
}

export default connect(mapStatesToProps, null)(ReportPage);